package ai.jessie.web.documents

import ai.jessie.shared.documents.DocumentDetailResponse
import ai.jessie.shared.documents.DocumentDetailsDto
import ai.jessie.shared.documents.DocumentFieldChangeResponse
import ai.jessie.shared.documents.DocumentHistoryEntryResponse
import ai.jessie.shared.documents.DocumentPaymentRequest
import ai.jessie.shared.documents.DocumentRemovalPreviewResponse
import ai.jessie.shared.documents.DocumentRemovalRequest
import ai.jessie.shared.documents.DocumentSaveRequest
import ai.jessie.shared.documents.DocumentTransitionRequest
import ai.jessie.shared.documents.DocumentVersionRequest
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

enum class DocumentStep {
    TRANSITION,
    PAYMENT,
    CLEAR_PAYMENT,
    REMOVAL,
}

// An open confirmation (BR-136). `changes` is the previewed outcome (step 9); `input` is the
// reason (A9-A11) or the payment date (A6).
data class DocumentConfirmation(
    val step: DocumentStep,
    val target: String? = null,
    val changes: List<DocumentFieldChangeResponse> = emptyList(),
    val removal: DocumentRemovalPreviewResponse? = null,
    val input: String = "",
)

// `draft` holds the shown, not yet saved details (steps 4-5); `problems` the corrections by field.
data class DocumentWorkflowUiState(
    val detail: DocumentDetailResponse? = null,
    val draft: DocumentDetailsDto? = null,
    val history: List<DocumentHistoryEntryResponse>? = null,
    val confirmation: DocumentConfirmation? = null,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
    val problems: Map<String, String> = emptyMap(),
    val notice: String? = null,
    val removed: Boolean = false,
) {
    val dirty: Boolean
        get() = detail != null && draft != null && draft != detail.details
}

data class DocumentWorkflowActions(
    val refresh: () -> Unit,
    val edit: ((DocumentDetailsDto) -> DocumentDetailsDto) -> Unit,
    val save: () -> Unit,
    val discard: () -> Unit,
    val transition: (String) -> Unit,
    val payment: () -> Unit,
    val clearPayment: () -> Unit,
    val remove: () -> Unit,
    val input: (String) -> Unit,
    val confirm: () -> Unit,
    val cancel: () -> Unit,
)

/**
 * UC-020 processing of one document. Single-flight; the page scope is keyed by organisation and
 * document, so a disposed scope never publishes another tenant's answer. Every write carries the
 * shown version (A14) and unsaved edits only travel with an explicit save or a confirmed transition
 * (B7); a cancelled confirmation discards them (A13). [changed] refreshes the list after a recorded
 * change; [lost] hands a session or organisation loss to the page (UC-006).
 */
class DocumentWorkflowState(
    private val port: DocumentWorkflowPort,
    private val scope: CoroutineScope,
    private val organisationId: String,
    private val documentId: String,
    private val changed: () -> Unit,
    private val lost: (OnboardingProblem) -> Unit,
) {
    var state by mutableStateOf(DocumentWorkflowUiState())
        private set

    val actions =
        DocumentWorkflowActions(
            refresh = ::refresh,
            edit = { change ->
                val draft = state.draft
                if (!state.busy && state.confirmation == null && draft != null)
                    state = state.copy(draft = change(draft), notice = null)
            },
            save = ::save,
            discard = {
                if (!state.busy && state.confirmation == null)
                    state =
                        state.copy(
                            draft = state.detail?.details,
                            problems = emptyMap(),
                            error = null,
                            notice = "Änderungen verworfen.",
                        )
            },
            transition = ::requestTransition,
            // BR-134: a new payment proposes organisation-local today; a correction its date.
            payment = {
                state.detail?.let {
                    open(DocumentConfirmation(DocumentStep.PAYMENT, input = it.paidOn ?: it.today))
                }
            },
            clearPayment = { open(DocumentConfirmation(DocumentStep.CLEAR_PAYMENT)) },
            remove = ::requestRemoval,
            input = { value ->
                state.confirmation?.let {
                    if (!state.busy) state = state.copy(confirmation = it.copy(input = value))
                }
            },
            confirm = ::confirm,
            cancel = ::cancel,
        )

    // Unsaved edits travel only with a save or a confirmed transition (B7).
    private val pendingDetails
        get() = state.draft.takeIf { state.dirty }

    // Steps 1-3 and 12. An unusable ID sends nothing and looks like any other absence (A4).
    fun refresh() {
        if (!validDocumentId(documentId)) {
            state = DocumentWorkflowUiState(error = unavailableDocumentProblem())
            return
        }
        request(writing = false) {
            val detail = port.detail(organisationId, documentId)
            currentCoroutineContext().ensureActive()
            val history = port.history(organisationId, documentId)
            currentCoroutineContext().ensureActive()
            state =
                state.copy(
                    detail = detail,
                    draft = detail.details,
                    history = history,
                    confirmation = null,
                )
        }
    }

    // B7: the versioned save of the shown details.
    private fun save() {
        val detail = state.detail
        val draft = state.draft?.takeIf { state.dirty && state.confirmation == null }
        if (detail == null || draft == null) return
        request(writing = true) {
            applied(
                port.save(organisationId, documentId, DocumentSaveRequest(detail.version, draft)),
                "Angaben gespeichert.",
            )
        }
    }

    // Steps 7-9, A1, A9, A10: only the server's permitted targets. A reason-free transition shows
    // the validated outcome first; nothing is applied before confirmation (A13).
    private fun requestTransition(target: String) {
        val detail = state.detail
        if (detail == null || state.busy || state.confirmation != null) return
        if (target !in detail.transitions) return
        if (reasonRequired(detail.status, target))
            open(DocumentConfirmation(DocumentStep.TRANSITION, target))
        else
            request(writing = false) {
                val preview =
                    port.previewTransition(
                        organisationId,
                        documentId,
                        DocumentTransitionRequest(detail.version, target, pendingDetails),
                    )
                currentCoroutineContext().ensureActive()
                state =
                    state.copy(
                        confirmation =
                            DocumentConfirmation(DocumentStep.TRANSITION, target, preview.changes)
                    )
            }
    }

    // A11: the server checks eligibility before the irreversible outcome is shown.
    private fun requestRemoval() {
        if (state.busy || state.confirmation != null || state.detail == null) return
        request(writing = false) {
            val preview = port.removalPreview(organisationId, documentId)
            currentCoroutineContext().ensureActive()
            state =
                state.copy(
                    confirmation = DocumentConfirmation(DocumentStep.REMOVAL, removal = preview)
                )
        }
    }

    private fun open(confirmation: DocumentConfirmation) {
        if (state.busy || state.confirmation != null || state.detail == null) return
        if (
            state.dirty &&
                (confirmation.step == DocumentStep.PAYMENT ||
                    confirmation.step == DocumentStep.CLEAR_PAYMENT)
        )
            return
        state =
            state.copy(
                confirmation = confirmation,
                error = null,
                problems = emptyMap(),
                notice = null,
            )
    }

    // Steps 10-11, A6-A11: one confirmed change, applied completely by the server or not at all.
    private fun confirm() {
        val detail = state.detail
        val confirmation = state.confirmation
        if (detail == null || confirmation == null || state.busy) return
        val target = confirmation.target.orEmpty()
        request(writing = true) {
            when (confirmation.step) {
                DocumentStep.TRANSITION -> {
                    val reason = confirmation.input.takeIf { reasonRequired(detail.status, target) }
                    val request =
                        DocumentTransitionRequest(detail.version, target, pendingDetails, reason)
                    applied(
                        port.transition(organisationId, documentId, request),
                        transitionNotice(detail.status, target),
                    )
                }
                DocumentStep.PAYMENT -> {
                    val request =
                        DocumentPaymentRequest(detail.version, confirmation.input.ifBlank { null })
                    val paid = port.recordPayment(organisationId, documentId, request)
                    applied(paid, paymentNotice(detail, paid))
                }
                DocumentStep.CLEAR_PAYMENT ->
                    applied(
                        port.clearPayment(
                            organisationId,
                            documentId,
                            DocumentVersionRequest(detail.version),
                        ),
                        "Die Zahlung wurde entfernt.",
                    )
                // The removed document leaves the page; its audit stays on the server.
                DocumentStep.REMOVAL -> {
                    val version = confirmation.removal?.version ?: detail.version
                    port.remove(
                        organisationId,
                        documentId,
                        DocumentRemovalRequest(version, confirmation.input),
                    )
                    currentCoroutineContext().ensureActive()
                    state =
                        DocumentWorkflowUiState(
                            busy = true,
                            removed = true,
                            notice = "„${detail.fileName}“ wurde endgültig gelöscht.",
                        )
                    changed()
                }
            }
        }
    }

    // A13 discards the presented edits; A12 records a cancelled removal on the server.
    private fun cancel() {
        val confirmation = state.confirmation
        if (confirmation == null || state.busy) return
        val closed = state.copy(confirmation = null, problems = emptyMap(), error = null)
        when (confirmation.step) {
            DocumentStep.TRANSITION ->
                state =
                    closed.copy(
                        draft = state.detail?.details,
                        notice =
                            "Abgebrochen. Status und gespeicherte Angaben sind unverändert; nicht " +
                                "gespeicherte Änderungen wurden verworfen.",
                    )
            DocumentStep.REMOVAL ->
                request(writing = true) {
                    port.cancelRemoval(organisationId, documentId)
                    currentCoroutineContext().ensureActive()
                    state =
                        state.copy(
                            confirmation = null,
                            notice = "Löschen abgebrochen. Das Dokument bleibt unverändert.",
                        )
                }
            DocumentStep.PAYMENT,
            DocumentStep.CLEAR_PAYMENT ->
                state = closed.copy(notice = "Abgebrochen. Die Zahlung wurde nicht geändert.")
        }
    }

    // A recorded change replaces the shown document; the list and history follow it.
    private suspend fun applied(detail: DocumentDetailResponse, notice: String) {
        currentCoroutineContext().ensureActive()
        state =
            state.copy(
                detail = detail,
                draft = detail.details,
                confirmation = null,
                notice = notice,
            )
        changed()
        reloadHistory()
    }

    // A history that cannot be loaded is shown as unavailable, never as a failed change.
    private suspend fun reloadHistory() {
        val history =
            try {
                port.history(organisationId, documentId)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (_: Exception) {
                null
            }
        currentCoroutineContext().ensureActive()
        state = state.copy(history = history)
    }

    private fun request(writing: Boolean, block: suspend () -> Unit) {
        if (state.busy) return
        state = state.copy(busy = true, error = null, problems = emptyMap(), notice = null)
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: DocumentRequestFailure) {
                currentCoroutineContext().ensureActive()
                rejected(state.refusedBy(failure, writing))
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                // An unexpected answer to a write leaves its outcome unknown (A15).
                state =
                    state.copy(error = if (writing) uncertainProblem() else unavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private suspend fun rejected(refusal: DocumentRefusal) {
        state = refusal.state
        refusal.lost?.let(lost)
        if (refusal.reloadHistory) reloadHistory()
        if (!refusal.refreshChoices) return
        // A3: only the choice lists of the shown version; a newer version is A14.
        val fresh =
            try {
                port.detail(organisationId, documentId)
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (_: Exception) {
                null
            }
        currentCoroutineContext().ensureActive()
        val shown = state.detail
        if (fresh != null && shown != null)
            state =
                if (fresh.version == shown.version)
                    state.copy(
                        detail = shown.copy(categories = fresh.categories, folders = fresh.folders)
                    )
                else
                    state.copy(
                        detail = fresh,
                        draft = fresh.details,
                        error = staleProblem("DOCUMENT_CHANGED", fresh),
                    )
    }
}

private fun transitionNotice(from: String, to: String): String {
    val left = if (from == INBOX) " Es ist nicht mehr in der Inbox." else ""
    return "Status geändert: Das Dokument ist jetzt ${documentStatusLabel(to)}.$left"
}

private fun paymentNotice(before: DocumentDetailResponse, after: DocumentDetailResponse): String {
    val date = after.paidOn.orEmpty()
    return when {
        before.paidOn != null -> "Das Zahlungsdatum wurde auf $date korrigiert."
        after.linkedInvoice ->
            "Zahlung vom $date erfasst. Die verknüpfte Rechnung ist mit demselben Datum bezahlt."
        else -> "Zahlung vom $date erfasst."
    }
}
